import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userAccount } from "../../../store/auth/selector";
import { RootState } from "../../../store/config/types";
import { Paper, Button, FormControl, FormLabel, Radio, RadioGroup, FormControlLabel } from "@material-ui/core";
import { Formik, Form, Field, useFormikContext } from "formik";
import { FormTextField } from "../../common/forms/FormTextField"; // Ajusta la ruta según tu estructura
import { History } from "history";


interface Diet {
  title: string;
  carb?: string;
  protein?: string;
  fats?: string;
  description: string;
}

const DEFAULT_DIET: Diet = {
  title: "Dieta Equilibrada 🌱🔄",
  carb: "45-55%",
  protein: "15-25%",
  fats: "20-35%",
  description:
    "La más recomendada para quienes buscan un enfoque saludable y sostenible.",
};

interface Values {
  biologicalSex: string;
  objective: string;
  dailyExercise: string;
  weight: string;
  height: string;
  age: string;
}

interface MultiStepFormProps {
  history: History;
  profileKey: string;
}

const MultiStepForm: React.FC<MultiStepFormProps> = ({ history, profileKey }) => {
  const actualUser = useSelector((state: RootState) => userAccount(state));
  const defaultInitialValues: Values = {
    biologicalSex: "",
    objective: "",
    dailyExercise: "",
    weight: "",
    height: "",
    age: "",
  };

  const savedValues = localStorage.getItem("multiStepFormValues");
  const initialFormValues: Values = savedValues ? JSON.parse(savedValues) : defaultInitialValues;

  const savedStep = localStorage.getItem("multiStepFormStep");
  const [step, setStep] = useState<number>(savedStep ? parseInt(savedStep, 10) : 0);

  useEffect(() => {
    localStorage.setItem("multiStepFormStep", step.toString());
  }, [step]);

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const submitForm = (values: Values) => {
    console.log("Datos del formulario:", JSON.stringify(values, null, 2));

    const weight = parseFloat(values.weight);
    const height = parseFloat(values.height);
    const age = parseFloat(values.age);
    const sex = values.biologicalSex === "hombre" ? 0 : 1;

    let goals: number;
    if (values.objective === "perder grasa") {
      goals = 0;
    } else if (values.objective === "mantener peso") {
      goals = 1;
    } else if (values.objective === "ganar musculo") {
      goals = 2;
    } else {
      goals = 1;
    }

    let actualExcercise: number;
    switch (values.dailyExercise) {
      case "poco o nada":
        actualExcercise = 0;
        break;
      case "ligero":
        actualExcercise = 1;
        break;
      case "moderado":
        actualExcercise = 2;
        break;
      case "intenso":
        actualExcercise = 3;
        break;
      case "muy intenso":
        actualExcercise = 4;
        break;
      default:
        actualExcercise = 0;
    }

    const body = { sex, goals, actualExcercise, weight, height, age };

    const actualExcerciseRatio: Record<number, number> = {
      0: 1.2,
      1: 1.375,
      2: 1.55,
      3: 1.725,
      4: 1.9,
    };
    const goalMap: Record<number, number> = {
      0: -250,
      1: 0,
      2: 250,
    };
    const macros = {
      protein: [0.35, 4],
      carbo: [0.3, 4],
      fats: [0.35, 9],
    };

    let bmr = 0;
    if (body.sex === 0) {
      bmr = (88.4 + 13.4 * body.weight) + (4.8 * body.height) - (5.68 * body.age);
    } else if (body.sex === 1) {
      bmr = (447.6 + 9.25 * body.weight) + (3.10 * body.height) - (4.33 * body.age);
    } else {
      bmr = (88.4 + 13.4 * body.weight) + (4.8 * body.height) - (5.68 * body.age);
    }
    const adjustedBmr = bmr * actualExcerciseRatio[body.actualExcercise];
    const diaryCaloriesByGoal = adjustedBmr + goalMap[body.goals];
    const imc = parseFloat((body.weight / Math.pow(body.height / 100, 2)).toFixed(2));
    const recommendedWater = parseFloat((((body.weight / 7) * 250) / 1000).toFixed(2));
    const recommendedDailyProteins = parseInt(
      ((diaryCaloriesByGoal * macros.protein[0]) / macros.protein[1]).toString()
    );
    const recommendedDailyCarbos = parseInt(
      ((diaryCaloriesByGoal * macros.carbo[0]) / macros.carbo[1]).toString()
    );
    const recommendedDailyFats = parseInt(
      ((diaryCaloriesByGoal * macros.fats[0]) / macros.fats[1]).toString()
    );
    const recommendedCaloriesPerDay = parseInt(diaryCaloriesByGoal.toString());

    const updatedUserData = {
      actualExcercise: body.actualExcercise,
      goals: body.goals,
      height: body.height,
      sex: body.sex,
      actualWeight: body.weight,
      age: body.age,
      bmr: parseInt(adjustedBmr.toString()),
      imc: imc,
      recommendedCaloriesPerDay: recommendedCaloriesPerDay,
      recommendedDailyCarbos: recommendedDailyCarbos,
      recommendedDailyFats: recommendedDailyFats,
      recommendedDailyProteins: recommendedDailyProteins,
      recommendedDailyWater: recommendedWater,
      firstTimeDashboard: false,
      selectedDiet: DEFAULT_DIET,
      email: actualUser.email,
    };

    console.log("Updated User Data:", updatedUserData);
    // Guardar perfil con la clave dinámica
    localStorage.setItem(profileKey, JSON.stringify(updatedUserData));
    window.location.reload();
  };

  const FormObserver = () => {
    const { values } = useFormikContext<Values>();
    useEffect(() => {
      localStorage.setItem("multiStepFormValues", JSON.stringify(values));
    }, [values]);
    return null;
  };

  return (
    <Paper className="user-form" elevation={0} style={{ margin: "0px", padding: "0px" }}>
      <div className="heading-line">
        <h2>Formulario de Información</h2>
      </div>
      <Formik
        initialValues={initialFormValues}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          if (step === 1) {
            submitForm(values);
            setSubmitting(false);
          } else {
            handleNext();
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="form" autoComplete="off">
            <FormObserver />
            {step === 0 && (
              <div className="cont-form-step0">
                <div className="field-group">
                  <FormControl component="fieldset">
                    <FormLabel component="legend" className="field-label">
                      Sexo biológico
                    </FormLabel>
                    <Field name="biologicalSex">
                      {({ field }: { field: any }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel value="hombre" control={<Radio />} label="Hombre" />
                          <FormControlLabel value="mujer" control={<Radio />} label="Mujer" />
                        </RadioGroup>
                      )}
                    </Field>
                  </FormControl>
                </div>
                <div className="field-group">
                  <FormControl component="fieldset">
                    <FormLabel component="legend" className="field-label">
                      Objetivo
                    </FormLabel>
                    <Field name="objective">
                      {({ field }: { field: any }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel value="ganar musculo" control={<Radio />} label="Ganar músculo" />
                          <FormControlLabel value="mantener peso" control={<Radio />} label="Mantener peso" />
                          <FormControlLabel value="perder grasa" control={<Radio />} label="Perder grasa" />
                        </RadioGroup>
                      )}
                    </Field>
                  </FormControl>
                </div>
                <div className="field-group">
                  <FormControl component="fieldset">
                    <FormLabel component="legend" className="field-label">
                      Ejercicio diario
                    </FormLabel>
                    <Field name="dailyExercise">
                      {({ field }: { field: any }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel value="poco o nada" control={<Radio />} label="Poco o nada" />
                          <FormControlLabel value="ligero" control={<Radio />} label="Ligero" />
                          <FormControlLabel value="moderado" control={<Radio />} label="Moderado" />
                          <FormControlLabel value="intenso" control={<Radio />} label="Intenso" />
                          <FormControlLabel value="muy intenso" control={<Radio />} label="Muy intenso" />
                        </RadioGroup>
                      )}
                    </Field>
                  </FormControl>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="cont-form-step1">
                <div className="field-group">
                  <FormControl component="fieldset">
                    <FormLabel component="legend" className="field-label">
                      Peso (kg)
                    </FormLabel>
                    <Field
                      name="weight"
                      component={FormTextField}
                      placeholder="Ingrese su peso en kg"
                    />
                  </FormControl>
                </div>
                <div className="field-group">
                  <FormControl component="fieldset">
                    <FormLabel component="legend" className="field-label">
                      Altura (cm)
                    </FormLabel>
                    <Field
                      name="height"
                      component={FormTextField}
                      placeholder="Ingrese su altura en cm"
                    />
                  </FormControl>
                </div>
                <div className="field-group">
                  <FormControl component="fieldset">
                    <FormLabel component="legend" className="field-label">
                      Edad
                    </FormLabel>
                    <Field
                      name="age"
                      component={FormTextField}
                      placeholder="Ingrese su edad"
                    />
                  </FormControl>
                </div>
              </div>
            )}
            <div
              className="buttons"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-evenly",
                padding: "30px",
              }}
            >
              {step > 0 && (
                <Button onClick={handleBack} variant="contained" color="secondary">
                  Volver
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disableElevation
                disabled={isSubmitting}
              >
                {step === 1 ? "Enviar" : "Siguiente"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};

export default MultiStepForm;
